import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { PRESET_GALLERY, jobCoverSrc, categoryCover } from "../../lib/covers";
import { Upload, Check, RotateCcw, Loader2 } from "lucide-react";

const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Escolha da imagem que aparece nas vagas: foto própria, galeria do setor ou capa da empresa.
 *
 * Props:
 *  - job: objeto da vaga (com job.id, job.category, job.cover)
 *  - open, onOpenChange, onSaved
 *  - apiPrefix: prefixo do endpoint. "company" (default) ou "admin"
 */
export const JobCoverDialog = ({ job, open, onOpenChange, onSaved, apiPrefix = "company" }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(null); // baseKey do setor expandido

  if (!job) return null;
  const currentSrc = preview || jobCoverSrc(job);
  const source = job.cover?.source;
  const basePath = `/${apiPrefix}/jobs/${job.id}/cover`;

  const done = (data, msg) => {
    toast.success(msg);
    onSaved?.(data?.cover);
    setPreview(null);
    onOpenChange(false);
  };

  const upload = async (file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type)) {
      toast.error("A imagem tem de ser PNG, JPG ou WEBP");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("A imagem não pode exceder 5 MB");
      return;
    }
    setBusy(true);
    setPreview(URL.createObjectURL(file));
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post(basePath, body);
      done(data, "Imagem da vaga atualizada");
    } catch (e) {
      toast.error(formatErr(e));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const pickPreset = async (key) => {
    setBusy(true);
    try {
      const { data } = await api.post(`${basePath}-preset`, { preset: key });
      done(data, "Foto da galeria aplicada");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(basePath);
      done(data, "A vaga volta a usar a capa da empresa");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  // Agrupa PRESET_GALLERY por baseKey
  const byCategory = PRESET_GALLERY.reduce((acc, p) => {
    (acc[p.baseKey] = acc[p.baseKey] || []).push(p);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="job-cover-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tighter">Imagem da vaga</DialogTitle>
          <DialogDescription>
            Esta imagem aparece no cartão da vaga e no topo da página da vaga. Se não escolheres
            nada, usamos a capa da empresa e, na falta dela, uma foto do setor.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl overflow-hidden border border-black/5 bg-slate-100 h-48">
          <img src={currentSrc} alt="Pré-visualização da imagem da vaga"
            data-testid="job-cover-preview"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.src = categoryCover(job.category, job.id); }} />
        </div>
        <p className="text-xs text-slate-500 -mt-2">
          A usar:{" "}
          <span className="font-semibold text-slate-700" data-testid="job-cover-source">
            {source === "upload" ? "foto carregada"
              : source === "external" ? "foto do site de origem"
              : source === "preset" ? "foto da galeria"
              : source === "company" ? "capa da empresa"
              : "foto do setor"}
          </span>
        </p>

        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            data-testid="job-cover-file-input"
            onChange={(e) => upload(e.target.files?.[0])} />
          <Button data-testid="job-cover-upload-btn" disabled={busy} className="rounded-full bg-slate-900 hover:bg-slate-800"
            onClick={() => fileRef.current?.click()}>
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Carregar foto
          </Button>
          <Button data-testid="job-cover-reset-btn" disabled={busy} variant="outline" className="rounded-full" onClick={reset}>
            <RotateCcw className="w-4 h-4 mr-2" /> Repor (usar capa da empresa)
          </Button>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">
            Ou escolhe da galeria por setor
          </p>
          <div className="space-y-3">
            {Object.entries(byCategory).map(([baseKey, items]) => {
              const isOpen = galleryOpen === baseKey;
              const catLabel = items[0]?.categoryLabel;
              return (
                <div key={baseKey} className="rounded-lg border border-black/5 overflow-hidden">
                  <button type="button" onClick={() => setGalleryOpen(isOpen ? null : baseKey)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 text-left">
                    <span className="text-sm font-semibold text-slate-800">{catLabel}</span>
                    <span className="text-[11px] text-slate-500">{items.length} fotos</span>
                  </button>
                  {isOpen && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-2 bg-white">
                      {items.map((p) => {
                        const active = job.cover?.source === "preset" && job.cover?.preset === p.key;
                        return (
                          <button key={p.key} type="button" disabled={busy}
                            data-testid={`job-cover-preset-${p.key}`}
                            onClick={() => pickPreset(p.key)}
                            className={`relative rounded-lg overflow-hidden border-2 transition-colors ${
                              active ? "border-amber-500" : "border-transparent hover:border-amber-300"
                            }`}>
                            <img src={p.url} alt={p.label} className="w-full h-24 object-cover" />
                            {active && (
                              <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                                <Check className="w-3 h-3 text-white" />
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default JobCoverDialog;
